import React from 'react';
import {
  ActionList,
  ActionListItem,
  Box,
  DatePicker,
  Dropdown,
  DropdownOverlay,
  Radio,
  RadioGroup,
  SelectInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { EmployeeFieldValueType, FieldPreviewState } from '../../../types';

const getPreviewPlaceholder = (valueType: EmployeeFieldValueType | '') => {
  switch (valueType) {
    case 'INTEGER':
      return 'e.g. 123';
    case 'DECIMAL':
      return 'e.g. 12.5';
    case 'CURRENCY':
      return 'e.g. 50000';
    case 'DATE':
      return '';
    default:
      return 'e.g. ACME-0481';
  }
};

const getSanitizedPreviewValue = (valueType: EmployeeFieldValueType | '', rawValue: string) => {
  switch (valueType) {
    case 'INTEGER':
      return rawValue.replace(/[^\d]/g, '');
    case 'DECIMAL':
    case 'CURRENCY':
      return rawValue.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
    default:
      return rawValue;
  }
};

type FieldPreviewProps = {
  label: string;
  valueType: EmployeeFieldValueType | '';
  enumValues: string[];
  preview: FieldPreviewState;
  onPreviewChange: (nextPreview: Partial<FieldPreviewState>) => void;
};

export const FieldPreview = ({
  label,
  valueType,
  enumValues,
  preview,
  onPreviewChange,
}: FieldPreviewProps) => {
  const previewAriaLabel = `${label} preview`;

  if (valueType === 'FILE') {
    return (
      <Box
        borderWidth="thin"
        borderStyle="dashed"
        borderColor="surface.border.gray.muted"
        borderRadius="medium"
        backgroundColor="surface.background.gray.intense"
        padding="spacing.4">
        <Text weight="semibold">File upload preview</Text>
        <Text marginTop="spacing.1" size="small" color="surface.text.gray.muted">
          Not implemented for file upload fields yet.
        </Text>
      </Box>
    );
  }

  if (valueType === 'BOOLEAN') {
    return (
      <RadioGroup
        label={label}
        name="employee-field-preview-boolean"
        value={preview.booleanValue}
        onChange={({ value }) => onPreviewChange({ booleanValue: value as 'YES' | 'NO' })}>
        <Box display="flex" gap="spacing.4">
          <Radio value="YES">Yes</Radio>
          <Radio value="NO">No</Radio>
        </Box>
      </RadioGroup>
    );
  }

  if (valueType === 'ENUM') {
    return (
      <Dropdown selectionType="single">
        <SelectInput
          label={label}
          accessibilityLabel={previewAriaLabel}
          placeholder={enumValues.length === 0 ? 'No values added' : 'Select a value'}
          value={preview.textValue}
          onChange={({ values }) => onPreviewChange({ textValue: values[0] ?? '' })}
          isDisabled={enumValues.length === 0}
        />
        <DropdownOverlay>
          <ActionList>
            {enumValues.map((enumValue) => (
              <ActionListItem key={enumValue} title={enumValue} value={enumValue} />
            ))}
          </ActionList>
        </DropdownOverlay>
      </Dropdown>
    );
  }

  if (valueType === 'DATE') {
    return (
      <DatePicker
        selectionType="single"
        label={label}
        accessibilityLabel={previewAriaLabel}
        value={preview.dateValue}
        onChange={(dateValue) => onPreviewChange({ dateValue })}
        inputPlaceHolder="DD/MM/YYYY"
        size="medium"
      />
    );
  }

  return (
    <TextInput
      label={label}
      accessibilityLabel={previewAriaLabel}
      type={
        valueType === 'INTEGER' || valueType === 'DECIMAL' || valueType === 'CURRENCY'
          ? 'number'
          : 'text'
      }
      placeholder={getPreviewPlaceholder(valueType)}
      value={preview.textValue}
      onChange={({ value }) =>
        onPreviewChange({ textValue: getSanitizedPreviewValue(valueType, value ?? '') })
      }
    />
  );
};
